/**
 * ergonix-out-of-stock.js
 *
 * Drives the Out-of-Stock indicator rendered by
 * snippets/ergonix-out-of-stock-card.liquid, the swatch sold-out state,
 * and provides ErgonixOOS.isVariantSoldOut() used by cart.js to refuse
 * adding sold-out variants.
 *
 * Public API:
 *   ErgonixOOS.show(cardEl, ctx)              — display the card
 *   ErgonixOOS.hide(cardEl)                   — hide the card
 *   ErgonixOOS.isVariantSoldOut(variantId)    — sold-out lookup across
 *                                               all known variant data
 *
 * Back-in-stock signup: the card's "Notify me" button (rendered when the
 * section passes omnisend_form_id) opens an Omnisend custom-trigger form
 * via the delegated click handler below.
 *
 * An Omnisend form cannot carry a product: it has no hidden fields, only a
 * fixed tag. So every signup used to land under one flat back_in_stock tag
 * with nothing saying what the person wanted, and a restock of any one thing
 * mailed the whole list. We therefore record the variant ourselves: remember
 * what the button was clicked for, wait for Omnisend to confirm the
 * subscriber, and post the pair to the API. Omnisend still owns the
 * subscription and the consent; we only store which variant was asked about,
 * and the restock email is sent from the real warehouse number, not from
 * Shopify's availability flag.
 */
(function () {
  'use strict';

  var BIS_SIGNUP_URL = 'https://dpd.gals.lt/api/back-in-stock/signup';

  function show(card, ctx) {
    if (!card) return;
    card.hidden = false;
    card.classList.add('ergonix-oos--active');
    if (ctx && ctx.variantId) {
      card.setAttribute('data-variant-id', ctx.variantId);
    }
  }

  function hide(card) {
    if (!card) return;
    card.hidden = true;
    card.classList.remove('ergonix-oos--active');
    card.removeAttribute('data-variant-id');
  }

  /* ─── Sold-out lookup ────────────────────────────────────────────
     Returns true when the variant is tracked AND its quantity is ≤ 0.
     Searches every variant data source the theme exposes. */
  function findVariant(variantId) {
    if (!variantId) return null;
    var id = String(variantId);
    var sources = [
      window.productVariantsData,
      window.deskVariantsData,
      window.upsellVariants && window.upsellVariants.drawer,
      window.upsellVariants && window.upsellVariants.chair
    ];
    for (var i = 0; i < sources.length; i++) {
      var arr = sources[i];
      if (!arr) continue;
      for (var j = 0; j < arr.length; j++) {
        if (String(arr[j].id) === id) return arr[j];
      }
    }
    return null;
  }

  function isVariantSoldOut(variantId) {
    var v = findVariant(variantId);
    if (!v) return false; // unknown variant — don't block
    // Respect Shopify's per-variant policy: 'continue' means the merchant
    // explicitly allows selling at 0 stock (Shopify's "Continue selling
    // when out of stock" checkbox). We never mark such variants as OOS —
    // Shopify's logic wins.
    if (v.inventory_policy === 'continue') return false;
    var qty = (typeof v.inventory_quantity === 'number') ? v.inventory_quantity : 0;
    var tracked = !!v.inventory_management;
    return tracked && qty <= 0;
  }

  /* ─── Upsell card sync ────────────────────────────────────────────
     Toggles the add-button + sold-out label on upsell cards based on
     the currently-active mini swatch. */
  function syncUpsellCard(card) {
    if (!card) return;
    var active = card.querySelector('.upsell-mini-dot.active');
    var addBtn = card.querySelector('.omnidesk-upsell__add, .product-page-upsell__add, .desk-upsell__add');
    if (!addBtn || !active) return;
    var soldOutLabel = (window.ergonixI18n && window.ergonixI18n.soldOut) || 'Išparduota';
    var isAvailable = active.getAttribute('data-available') !== 'false';
    if (isAvailable) {
      addBtn.disabled = false;
      addBtn.classList.remove('omnidesk-upsell__add--soldout');
      if (addBtn.dataset.origLabel) {
        addBtn.textContent = addBtn.dataset.origLabel;
        delete addBtn.dataset.origLabel;
      }
    } else {
      if (!addBtn.dataset.origLabel) {
        addBtn.dataset.origLabel = addBtn.textContent.trim();
      }
      addBtn.disabled = true;
      addBtn.classList.add('omnidesk-upsell__add--soldout');
      addBtn.textContent = soldOutLabel;
    }
  }

  /* ─── Back-in-stock notify button ────────────────────────────────
     Opens the Omnisend custom-trigger form (form ID comes from the
     data-omnisend-form attribute rendered by the card snippet). */
  /* Which variant the visitor last asked to be told about. Omnisend fires the
     same confirmation event for every one of its forms, the newsletter popup
     included, so an attribution is only trusted for a short window after the
     notify button itself was clicked. */
  var pendingVariantId = null;
  var pendingAt = 0;
  var PENDING_TTL_MS = 10 * 60 * 1000;

  var MARKET_BY_HOST = {
    'ergonix.lt': 'lt', 'ergonix.lv': 'lv', 'ergonix.ee': 'ee',
    'ergonix.cz': 'cz', 'ergonix.pl': 'pl', 'ergonix.fi': 'fi'
  };

  function currentMarket() {
    var host = (window.location.hostname || '').replace(/^www\./, '');
    return MARKET_BY_HOST[host] || '';
  }

  document.addEventListener('click', function (e) {
    var btn = e.target && e.target.closest && e.target.closest('.ergonix-oos__notify-btn');
    if (!btn) return;
    var formId = btn.getAttribute('data-omnisend-form');
    if (!formId) return;
    var card = btn.closest('.ergonix-oos');
    pendingVariantId = card && card.getAttribute('data-variant-id');
    pendingAt = Date.now();
    window.omnisend = window.omnisend || [];
    window.omnisend.push(['openForm', formId]);
  });

  /* Omnisend hands us the email once the form is submitted and the contact is
     identified. That is the only moment both halves are known. */
  window.addEventListener('omnisendForms', function (e) {
    var detail = e && e.detail;
    if (!detail || detail.name !== 'formSubscriberIdentified' || !detail.email) return;
    if (!pendingVariantId || (Date.now() - pendingAt) > PENDING_TTL_MS) return;

    var payload = {
      email: detail.email,
      variantId: pendingVariantId,
      market: currentMarket()
    };
    pendingVariantId = null;

    /* Fire and forget. The person is already subscribed in Omnisend by this
       point, so a failure here must never surface to them or block the form. */
    try {
      fetch(BIS_SIGNUP_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        keepalive: true
      }).catch(function () {});
    } catch (err) { /* older browsers: the signup still exists in Omnisend */ }
  });

  document.addEventListener('click', function (e) {
    var dot = e.target && e.target.closest && e.target.closest('.upsell-mini-dot');
    if (!dot) return;
    setTimeout(function () {
      var card = dot.closest('[data-upsell-card]') || dot.closest('.omnidesk-upsell__card') || dot.closest('.product-page-upsell__card');
      syncUpsellCard(card);
    }, 0);
  });

  document.addEventListener('DOMContentLoaded', function () {
    document.querySelectorAll('[data-upsell-card], .omnidesk-upsell__card, .product-page-upsell__card').forEach(syncUpsellCard);
  });

  window.ErgonixOOS = {
    show: show,
    hide: hide,
    findVariant: findVariant,
    isVariantSoldOut: isVariantSoldOut,
    syncUpsellCard: syncUpsellCard
  };
})();
